<template>
  <div class="survey-cake-container">
    <!-- 全畫面 Loading 遮罩 -->
    <div v-if="isGeneratingPDF" class="pdf-loading-overlay">
      <div class="loading-content">
        <div class="loading-spinner"></div>
      </div>
    </div>

    <!-- 下載按鈕 -->
    <div v-if="pdfReady" class="download-button-container">
      <button
        :class="['download-btn', { disabled: !pdfReady }]"
        :disabled="!pdfReady"
        @click="downloadPDF"
      >
        <span>點我下載測驗結果</span>
      </button>
    </div>

    <!-- 顯示的組件 -->
    <StartPage :name="name" :isPDF="false" />
    <ResultPage
      :name="name"
      :classifiedScore="classifiedScore"
      :resultScore="resultScore"
      :isPDF="false"
    />
    <IntelligenceFirstCard :resultScore="resultScore" :isPDF="false" />
    <IntelligenceSecondCard :resultScore="resultScore" :isPDF="false" />
    <EndPage :isPDF="false" />

    <!-- 隱藏的 PDF 預覽容器 -->
    <div class="pdf-preview-container" style="position: absolute">
      <div ref="startPagePDF" class="pdf-page">
        <StartPage :name="name" :isPDF="true" />
      </div>
      <div ref="resultPagePDF" class="pdf-page">
        <ResultPage
          :name="name"
          :classifiedScore="classifiedScore"
          :resultScore="resultScore"
          :isPDF="true"
        />
      </div>
      <div ref="intelligenceFirstCardPDF" class="pdf-page">
        <IntelligenceFirstCard :resultScore="resultScore" :isPDF="true" />
      </div>
      <div ref="intelligenceSecondCardPDF" class="pdf-page">
        <IntelligenceSecondCard :resultScore="resultScore" :isPDF="true" />
      </div>
      <div ref="endPagePDF" class="pdf-page">
        <EndPage :isPDF="true" />
      </div>
    </div>
  </div>
</template>

<script>
import API from "@/api";
import { sntoidData, classData } from "@/data/survey-cake-element-data.ts";
import StartPage from "@/components/surveyCake/StartPage.vue";
import ResultPage from "@/components/surveyCake/ResultPage.vue";
import IntelligenceFirstCard from "@/components/surveyCake/IntelligenceFirstCard.vue";
import IntelligenceSecondCard from "@/components/surveyCake/IntelligenceSecondCard.vue";
import EndPage from "@/components/surveyCake/EndPage.vue";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
const CryptoJS = require("crypto-js");

export default {
  name: "SurveyCake",
  components: {
    StartPage,
    ResultPage,
    IntelligenceFirstCard,
    IntelligenceSecondCard,
    EndPage,
  },
  data() {
    return {
      surveyData: null,
      svid: null,
      hash: null,
      name: null,
      sntoid: sntoidData,
      class: classData,
      questionScoreData: [],
      resultScore: [],
      classifiedScore: [],
      // PDF 相關狀態
      isGeneratingPDF: true,
      pdfReady: false,
      pdfData: null,
      generationTimeout: null,
    };
  },

  mounted() {
    this.parseURLParams();
    if (!this.svid || !this.hash) {
      this.$router.push({ name: "Login" });
    }
    this.fetchSurveyContent();
  },

  methods: {
    parseURLParams() {
      const urlParams = new URLSearchParams(window.location.search);
      this.svid = urlParams.get("svid");
      this.hash = urlParams.get("hash");
    },
    convertToScoreData() {
      // 建立 sn 到 id 的對應表
      const snToIdMap = {};
      this.sntoid.forEach((item) => {
        snToIdMap[item.sn] = item.id;
      });

      // 篩選並轉換資料
      const scoreMapping = {
        總是: 5,
        常常: 4,
        有時候: 3,
        很少: 2,
        從來沒有: 1,
      };

      const result = this.surveyData
        .filter((item) => item.type === "NESTCHILD")
        .map((item) => ({
          id: snToIdMap[item.sn],
          score: scoreMapping[item.answer[0]] || 0,
        }))
        .filter((item) => item.id !== undefined);

      this.questionScoreData = result;
    },
    calculateResultScore() {
      // 依照 class question 對應 questionScoreData id，計算各智能類型的總分
      this.resultScore = this.class.map((category) => {
        // 計算該類別所有問題的分數總和
        const total = category.question.reduce((sum, questionId) => {
          const scoreItem = this.questionScoreData.find(
            (item) => item.id === questionId
          );
          return sum + (scoreItem ? scoreItem.score : 0);
        }, 0);

        return {
          ...category,
          total: total,
        };
      });
    },
    classifyResultScore() {
      this.classifiedScore = [
        { id: 1, range: "29分以上", class: [] },
        { id: 2, range: "16~28分", class: [] },
        { id: 3, range: "15分以下", class: [] },
      ];

      this.resultScore.forEach((item) => {
        if (item.total >= 29) {
          this.classifiedScore[0].class.push(item.name);
        } else if (item.total >= 16) {
          this.classifiedScore[1].class.push(item.name);
        } else {
          this.classifiedScore[2].class.push(item.name);
        }
      });
    },
    async fetchSurveyContent() {
      try {
        // 呼叫API取得問卷內容
        const response = await API.GetSurveyContent(this.svid, this.hash);
        const decrypted = CryptoJS.AES.decrypt(
          response.data,
          CryptoJS.enc.Utf8.parse(process.env.VUE_APP_SURVEYCAKE_HASH_KEY),
          {
            iv: CryptoJS.enc.Utf8.parse(process.env.VUE_APP_SURVEYCAKE_IV_KEY),
          }
        );
        this.surveyData = JSON.parse(
          decrypted.toString(CryptoJS.enc.Utf8)
        ).result;

        const nameItem = this.surveyData.find(
          (item) => item.subject === "姓名"
        );
        this.name = nameItem.answer[0];

        // 轉換問卷資料為分數格式
        this.convertToScoreData();

        // 計算各智能類型的總分
        this.calculateResultScore();

        // 依據分數進行分類
        this.classifyResultScore();

        // 等待 DOM 更新後開始生成 PDF
        this.$nextTick(() => {
          this.generatePDF();
        });
      } catch (error) {
        console.error("載入問卷內容失敗:", error);
        this.handlePDFGenerationError();
      }
    },

    // 設定超時處理
    setupTimeout() {
      this.generationTimeout = setTimeout(() => {
        console.warn("PDF 生成超時，強制關閉 loading");
        this.isGeneratingPDF = false;
        this.pdfReady = false;
      }, 5000); // 增加到 5 秒，給雷達圖準備更多時間
    },

    // 清除超時計時器
    clearTimeout() {
      if (this.generationTimeout) {
        clearTimeout(this.generationTimeout);
        this.generationTimeout = null;
      }
    },

    // 處理 PDF 生成錯誤
    handlePDFGenerationError() {
      this.clearTimeout();
      this.isGeneratingPDF = false;
      this.pdfReady = false;
      console.error("PDF 生成失敗");
    },

    // 準備所有雷達圖的 PDF 圖片
    async prepareRadarChartsForPDF() {
      const radarChartPromises = [];

      // 查找所有 PDF 頁面中的 RadarChart 組件
      const pageRefs = [
        "startPagePDF",
        "resultPagePDF",
        "intelligenceFirstCardPDF",
        "intelligenceSecondCardPDF",
        "endPagePDF",
      ];

      for (const pageRef of pageRefs) {
        const pageElement = this.$refs[pageRef];
        if (pageElement) {
          // 查找該頁面中的 RadarChart 組件
          const radarCharts = this.findRadarChartComponents(pageElement);
          for (const radarChart of radarCharts) {
            if (radarChart.prepareForPDF) {
              radarChartPromises.push(radarChart.prepareForPDF());
            }
          }
        }
      }

      if (radarChartPromises.length > 0) {
        await Promise.all(radarChartPromises);

        // 等待一小段時間確保 DOM 更新
        await new Promise((resolve) => setTimeout(resolve, 200));
      }
    },

    // 查找頁面中的 RadarChart 組件
    findRadarChartComponents(element) {
      const radarCharts = [];

      // 遞歸查找所有 Vue 組件實例
      const findComponents = (vnode) => {
        if (vnode && vnode.$children) {
          vnode.$children.forEach((child) => {
            if (child.$options.name === "RadarChart") {
              radarCharts.push(child);
            }
            findComponents(child);
          });
        }
      };

      // 從頁面的 Vue 實例開始查找
      if (element.__vue__) {
        findComponents(element.__vue__);
      }

      return radarCharts;
    },

    // 生成 PDF
    async generatePDF() {
      try {
        this.setupTimeout();

        // 首先準備所有雷達圖的 PDF 圖片
        await this.prepareRadarChartsForPDF();

        const pdf = new jsPDF({
          orientation: "portrait",
          unit: "px",
          format: [2480, 3508],
        });

        const pageRefs = [
          "startPagePDF",
          "resultPagePDF",
          "intelligenceFirstCardPDF",
          "intelligenceSecondCardPDF",
          "endPagePDF",
        ];

        for (let i = 0; i < pageRefs.length; i++) {
          const element = this.$refs[pageRefs[i]];
          if (element) {
            // 先擷取原始 canvas
            const originalCanvas = await html2canvas(element, {
              width: 2480,
              height: 3508,
              scale: 1,
              useCORS: true,
              allowTaint: true,
              backgroundColor: null,
              logging: false,
              removeContainer: true,
            });

            // 創建新的 canvas 並強制調整到確切尺寸
            const resizedCanvas = document.createElement("canvas");
            resizedCanvas.width = 2480;
            resizedCanvas.height = 3508;
            const ctx = resizedCanvas.getContext("2d");

            // 將原始 canvas 內容繪製到新的 canvas 上，強制調整尺寸
            ctx.drawImage(originalCanvas, 0, 0, 2480, 3508);

            const imgData = resizedCanvas.toDataURL("image/png");

            if (i > 0) {
              pdf.addPage([2480, 3508]);
            }

            // 確保添加到 PDF 時使用正確尺寸
            pdf.addImage(imgData, "PNG", 0, 0, 2480, 3508);
          }
        }

        this.pdfData = pdf;
        this.pdfReady = true;
        this.isGeneratingPDF = false;
        this.clearTimeout();
      } catch (error) {
        console.error("PDF 生成過程中發生錯誤:", error);
        this.handlePDFGenerationError();
      }
    },

    // 下載 PDF
    downloadPDF() {
      if (this.pdfData && this.pdfReady) {
        const filename = `${this.name || "問卷結果"}_多元智能測驗結果.pdf`;
        this.pdfData.save(filename);
      }
    },
  },
};
</script>

<style scoped lang="scss">
.survey-cake-container {
  display: flex;
  flex-direction: column;
  position: relative;
}

/* PDF Loading 遮罩樣式 */
.pdf-loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 99999;
}

.loading-content {
  padding: 2rem;
  border-radius: 10px;
  text-align: center;
}

.loading-content p {
  margin-top: 1rem;
  font-size: 1.2rem;
  color: #333;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

@keyframes zoom-in {
  0% {
    transform: translateX(-50%) scale(0);
  }
  80% {
    transform: translateX(-50%) scale(1.1);
  }
  100% {
    transform: translateX(-50%) scale(1);
  }
}

/* 下載按鈕樣式 */
.download-button-container {
  position: fixed;
  bottom: 5%;
  left: 50%;
  transform: translateX(-50%) scale(0);
  z-index: 9999;
  animation: zoom-in 0.5s ease-in-out forwards;
  span {
    letter-spacing: 0.05em;
  }
}

.download-btn {
  background: #eb6165;
  color: white;
  border: none;
  padding: 12px 20px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 24px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
  text-wrap: nowrap;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
  @media only screen and (max-width: 768px) {
    font-size: 16px;
  }
}

.download-btn.disabled {
  background: #6c757d;
  cursor: not-allowed;
  opacity: 0.7;
}

/* PDF 頁面樣式 */
.pdf-page {
  width: 2480px;
  height: 3508px;
  margin-bottom: 20px;
}

.pdf-preview-container {
  pointer-events: none;
  left: -999999px;
  top: -999999px;
}
</style>
